import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';

function LocalizacaoLista() {
    const navigate = useNavigate();
    const [localizacaoList, setLocalizacaoList] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ valor: '', ativo: true, ordem: 0 });
    const [error, setError] = useState('');

    useEffect(() => {
        loadLocalizacaoList();
    }, []);

    const loadLocalizacaoList = async () => {
        setLoading(true);
        try {
            const response = await api.get('/localizacao-lista');
            setLocalizacaoList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de localização:', error);
            setError('Erro ao carregar lista de localização');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ valor: item.valor, ativo: item.ativo === 1, ordem: item.ordem || 0 });
        } else {
            setEditingItem(null);
            setFormData({ valor: '', ativo: true, ordem: 0 });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ valor: '', ativo: true, ordem: 0 });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.valor.trim()) {
            setError('Valor é obrigatório');
            return;
        }

        try {
            if (editingItem) {
                await api.put(`/localizacao-lista/${editingItem.id}`, formData);
            } else {
                await api.post('/localizacao-lista', formData);
            }
            handleCloseDialog();
            loadLocalizacaoList();
        } catch (error) {
            console.error('Erro ao salvar localização:', error);
            setError(error.response?.data?.error || 'Erro ao salvar localização');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta localização?')) {
            try {
                await api.delete(`/localizacao-lista/${id}`);
                loadLocalizacaoList();
            } catch (error) {
                console.error('Erro ao deletar localização:', error);
                alert('Erro ao deletar localização: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const localizacaoFiltradas = localizacaoList.filter(l => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return l.valor.toLowerCase().includes(searchLower);
    });

    return (
        <Container>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Gerenciar Localização
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Nova Localização
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar localização..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Valor</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell align="center">Ordem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : localizacaoFiltradas.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {search ? 'Nenhuma localização encontrada' : 'Nenhuma localização cadastrada'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            localizacaoFiltradas.map((localizacao) => (
                                <TableRow key={localizacao.id} hover>
                                    <TableCell>{localizacao.valor}</TableCell>
                                    <TableCell align="center">
                                        {localizacao.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell align="center">{localizacao.ordem || 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(localizacao)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(localizacao.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Localização' : 'Nova Localização'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Valor"
                            value={formData.valor}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Ordem"
                            type="number"
                            value={formData.ordem}
                            onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ativo}
                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default LocalizacaoLista;

